/**
 * MetadataDisplay Component
 *
 * A generic component for displaying key-value metadata in a clean, organized format.
 * Automatically formats field names from snake_case to Title Case.
 *
 * Features:
 * - Automatic label formatting (user_agent → User Agent)
 * - Grid layout for organized display
 * - Handles empty metadata gracefully
 * - Supports complex values (objects, arrays)
 * - Customizable styling
 */

import { Fragment, type CSSProperties } from 'react';

export interface MetadataDisplayProps {
  /** Metadata object with key-value pairs */
  metadata: Record<string, unknown>;
  /** Background color for the container (default: light violet) */
  backgroundColor?: string;
  /** Number of grid columns (default: 1 for single column layout) */
  gridColumns?: number;
  /** Custom container style */
  containerStyle?: CSSProperties;
  /** Custom label style */
  labelStyle?: CSSProperties;
  /** Custom value style */
  valueStyle?: CSSProperties;
}

/**
 * Format metadata field names from snake_case to Title Case
 *
 * Examples:
 * - "user_agent" → "User Agent"
 * - "ip_address" → "IP Address"
 * - "scan_id" → "Scan ID"
 * - "md5_hash" → "MD5 Hash"
 */
export function formatMetadataLabel(key: string): string {
  return key
    .split('_')
    .map((word) => {
      // Handle special acronyms
      const upperWord = word.toUpperCase();
      if (['ID', 'IP', 'MD5', 'URL', 'API', 'HTTP', 'HTTPS'].includes(upperWord)) {
        return upperWord;
      }
      // Regular title case
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
}

/**
 * Format metadata values for display
 */
function formatMetadataValue(value: unknown): string {
  if (value === null || value === undefined) {
    return 'N/A';
  }

  // For arrays, stringify as JSON (not common in audit logs)
  if (Array.isArray(value)) {
    return JSON.stringify(value, null, 2);
  }

  // For objects, don't stringify - they will be rendered recursively
  if (typeof value === 'object') {
    return ''; // Will be handled by nested rendering
  }

  return String(value);
}

/**
 * Check if a value is a nested object (not null, not array)
 */
function isNestedObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Generic metadata display component.
 *
 * Usage:
 * ```tsx
 * <MetadataDisplay
 *   metadata={{
 *     user_agent: 'Mozilla/5.0...',
 *     ip_address: '192.168.1.1',
 *     scan_id: 'scan-123'
 *   }}
 * />
 * ```
 */
export function MetadataDisplay({
  metadata,
  backgroundColor = 'rgba(81, 12, 255, 0.05)',
  gridColumns = 1,
  containerStyle,
  labelStyle,
  valueStyle,
}: MetadataDisplayProps) {
  const metadataEntries = Object.entries(metadata);

  // Default styles
  const defaultContainerStyle: CSSProperties = {
    padding: '16px 24px',
    fontSize: '14px',
    backgroundColor,
  };

  const defaultGridStyle: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: gridColumns === 1 ? '200px 1fr' : `repeat(${gridColumns}, 200px 1fr)`,
    gap: '12px 24px',
    alignItems: 'start',
  };

  const defaultLabelStyle: CSSProperties = {
    fontWeight: 600,
    color: '#666B71',
    fontSize: '14px',
  };

  const defaultValueStyle: CSSProperties = {
    color: '#232730',
    fontSize: '14px',
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap',
  };

  const emptyStateStyle: CSSProperties = {
    color: '#9CA1AB',
    fontStyle: 'italic',
    padding: '8px 0',
  };

  // Merge custom styles with defaults
  const finalContainerStyle = { ...defaultContainerStyle, ...containerStyle };
  const finalLabelStyle = { ...defaultLabelStyle, ...labelStyle };
  const finalValueStyle = { ...defaultValueStyle, ...valueStyle };

  return (
    <div style={finalContainerStyle}>
      {metadataEntries.length === 0 ? (
        <div style={emptyStateStyle}>No metadata available</div>
      ) : (
        <div style={defaultGridStyle}>
          {metadataEntries.map(([key, value]) => {
            // Check if value is a nested object that should be expanded
            if (isNestedObject(value)) {
              return (
                <Fragment key={key}>
                  {/* Parent key as a section header */}
                  <div
                    style={{
                      ...finalLabelStyle,
                      gridColumn: '1 / -1',
                      marginTop: '8px',
                      paddingBottom: '4px',
                      borderBottom: '1px solid #E1E2E6',
                      fontSize: '13px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    {formatMetadataLabel(key)}
                  </div>
                  {/* Nested fields with indentation */}
                  {Object.entries(value).map(([nestedKey, nestedValue]) => (
                    <Fragment key={`${key}.${nestedKey}`}>
                      <div style={{ ...finalLabelStyle, paddingLeft: '16px' }}>
                        {formatMetadataLabel(nestedKey)}:
                      </div>
                      <div style={finalValueStyle}>
                        {formatMetadataValue(nestedValue)}
                      </div>
                    </Fragment>
                  ))}
                </Fragment>
              );
            }

            // Regular flat key-value pair
            return (
              <Fragment key={key}>
                <div style={finalLabelStyle}>
                  {formatMetadataLabel(key)}:
                </div>
                <div style={finalValueStyle}>
                  {formatMetadataValue(value)}
                </div>
              </Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}
